import React, { useEffect, useState, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import MarketProgress from "@/components/MarketProgress";
import { toast } from "sonner";
import { BarChart3, Gauge, Star, Award, Users, RefreshCw, Loader2, TrendingUp, Lightbulb, ExternalLink, MapPin, Coffee, Waves, ListChecks, ChevronDown, ChevronUp, CalendarDays, ShieldCheck, ShieldAlert, Shield, Info, Plus, Ban, RotateCcw, Clock, AlertTriangle } from "lucide-react";

const POS_COLOR = { below: "var(--demand-low)", inline: "var(--demand-medium)", premium: "var(--demand-high)" };
const CONF = {
  high: { icon: ShieldCheck, color: "var(--demand-low)", it: "Alta affidabilità", en: "High reliability" },
  medium: { icon: Shield, color: "var(--demand-medium)", it: "Affidabilità media", en: "Medium reliability" },
  low: { icon: ShieldAlert, color: "var(--demand-high)", it: "Bassa affidabilità", en: "Low reliability" },
};

const isoDay = (d) => d.toISOString().slice(0, 10);
const defaultCheckIn = () => isoDay(new Date(Date.now() + 14 * 86400000));
const fmtDate = (iso, lang) => {
  if (!iso) return "";
  try { return new Date(iso + "T00:00:00").toLocaleDateString(lang === "it" ? "it-IT" : "en-US", { day: "numeric", month: "short" }); }
  catch { return iso; }
};
const fmtDateTime = (iso, lang) => {
  if (!iso) return "";
  try { return new Date(iso).toLocaleString(lang === "it" ? "it-IT" : "en-US", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }); }
  catch { return iso; }
};

export default function MarketPanel({ propId, variant = "full", onMarketChange }) {
  const { lang, loadProperties } = useApp();
  const navigate = useNavigate();
  const openDetail = (c) => {
    const cid = c.competitor_id || c.url;
    if (!cid) return;
    navigate(`/competitor?prop=${propId}&cid=${encodeURIComponent(cid)}&stay=`);
  };
  const addToMine = async (cid) => {
    try {
      const { data } = await api.post(`/properties/${propId}/competitors/add-to-mine`, { competitor_id: cid });
      if (data.already) toast.info(lang === "it" ? "Competitor già nella tua lista" : "Competitor already in your list");
      else { toast.success(lang === "it" ? "Aggiunto ai selezionati — prezzo ricalcolato" : "Added to selected — price recalculated"); await load(); loadProperties?.(); onMarketChange?.(); }
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(d || (lang === "it" ? "Errore" : "Error"));
    }
  };
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [showAnalyzed, setShowAnalyzed] = useState(false);
  const [showManage, setShowManage] = useState(false);
  const [checkIn, setCheckIn] = useState(defaultCheckIn());
  const [nights, setNights] = useState(1);
  const [busyId, setBusyId] = useState(null);
  const [addUrl, setAddUrl] = useState("");
  const [adding, setAdding] = useState(false);
  const pollRef = useRef(null);
  const L = (it, en) => (lang === "it" ? it : en);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/properties/${propId}/market-positioning?lang=${lang}`);
      setData(data);
      if (data?.window?.check_in) {
        setCheckIn(data.window.check_in);
        if (data.window.nights) setNights(data.window.nights);
      }
    }
    catch { setData(null); } finally { setLoading(false); }
  }, [propId, lang]);

  useEffect(() => { setLoading(true); load(); return () => pollRef.current && clearInterval(pollRef.current); }, [load]);

  const analyze = async () => {
    setRunning(true);
    try {
      const { data: start } = await api.post(`/properties/${propId}/market-intelligence`, { radius_km: 5, check_in: checkIn, nights, language: lang });
      if (start.status === "ready") { await load(); setRunning(false); toast.success(L("Mercato aggiornato", "Market updated")); return; }
      const ck = start.cache_key;
      pollRef.current = setInterval(async () => {
        try {
          const { data: st } = await api.get(`/properties/${propId}/market-intelligence/status?cache_key=${ck}`);
          if (st.status === "ready") { clearInterval(pollRef.current); await load(); setRunning(false); toast.success(L("Mercato aggiornato", "Market updated")); }
          else if (st.status === "error") { clearInterval(pollRef.current); setRunning(false); toast.error(L("Errore analisi mercato", "Market analysis error")); }
        } catch { /* keep polling */ }
      }, 5000);
    } catch { setRunning(false); toast.error(L("Errore", "Error")); }
  };

  const toggleExclude = async (cid, excluded) => {
    setBusyId(cid);
    try {
      await api.post(`/properties/${propId}/competitors/exclude`, { competitor_id: cid, excluded, lang });
      await load();
      loadProperties?.();
      onMarketChange?.();
      toast.success(excluded ? L("Competitor rimosso dal calcolo — prezzo ricalcolato", "Competitor removed from calc — price recalculated") : L("Competitor reincluso — prezzo ricalcolato", "Competitor re-included — price recalculated"));
    } catch { toast.error(L("Errore", "Error")); } finally { setBusyId(null); }
  };

  const addByUrl = async () => {
    const url = addUrl.trim();
    if (!url) return;
    setAdding(true);
    try {
      await api.post(`/properties/${propId}/competitors/add`, { booking_url: url, lang });
      setAddUrl("");
      await load();
      onMarketChange?.();
      toast.success(L("Competitor aggiunto", "Competitor added"));
    } catch (e) {
      toast.error(e?.response?.data?.detail || L("Impossibile aggiungere", "Could not add"));
    } finally { setAdding(false); }
  };

  const win = data?.window;
  const p = data?.positioning;
  const s = data?.stats;
  const prof = data?.profile;
  const conf = data?.confidence;
  const confMeta = conf ? CONF[conf.level] : null;
  const selected = data?.selected || [];
  const analyzed = data?.analyzed || [];

  return (
    <motion.div data-testid="market-panel" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.14 }}
      className="md:col-span-4 bg-card border border-border rounded-lg p-6">
      <div className="flex flex-wrap items-start justify-between gap-3 mb-5">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center shrink-0"><BarChart3 className="w-5 h-5 text-primary" /></div>
          <div>
            <h3 className="text-lg font-semibold leading-tight">{variant === "competitors" ? L("Competitor", "Competitors") : L("Mercato & Competitor", "Market & Competitors")}</h3>
            <p className="text-sm text-muted-foreground">{L("Competitor realmente simili · prezzo pubblico vs pubblico (Booking · Apify)", "Truly similar competitors · public vs public price (Booking · Apify)")}</p>
            <div className="flex flex-wrap items-center gap-2 mt-1.5">
              {win?.check_in && (
                <span data-testid="market-window" className="inline-flex items-center gap-1.5 text-xs font-medium text-primary bg-primary/10 rounded-full px-2.5 py-1">
                  <CalendarDays className="w-3.5 h-3.5" />
                  {fmtDate(win.check_in, lang)}–{fmtDate(win.check_out, lang)} · {win.nights} {L("notti", "nights")}
                </span>
              )}
              {data?.updated_at && (
                <span data-testid="market-updated" className="inline-flex items-center gap-1 text-xs text-muted-foreground">
                  <Clock className="w-3.5 h-3.5" />{L("Ultimo aggiornamento", "Last update")}: {fmtDateTime(data.updated_at, lang)}
                </span>
              )}
            </div>
          </div>
        </div>
        <div className="flex flex-wrap items-end gap-2">
          <div className="flex flex-col">
            <label className="text-[10px] uppercase tracking-wide text-muted-foreground font-bold mb-1">{L("Check-in", "Check-in")}</label>
            <input data-testid="market-checkin" type="date" value={checkIn} min={isoDay(new Date())}
              onChange={(e) => setCheckIn(e.target.value)}
              className="h-9 rounded-md border border-border bg-background px-2 text-sm font-data" />
          </div>
          <div className="flex flex-col">
            <label className="text-[10px] uppercase tracking-wide text-muted-foreground font-bold mb-1">{L("Notti", "Nights")}</label>
            <select data-testid="market-nights" value={nights} onChange={(e) => setNights(Number(e.target.value))}
              className="h-9 rounded-md border border-border bg-background px-2 text-sm font-data">
              {[1, 2, 3, 4, 5, 6, 7].map((n) => <option key={n} value={n}>{n}</option>)}
            </select>
          </div>
          <Button data-testid="market-analyze-btn" variant="outline" size="sm" className="h-9" onClick={analyze} disabled={running}>
            {running ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />{L("Analisi in corso...", "Analyzing...")}</> : <><RefreshCw className="w-4 h-4 mr-2" />{L("Aggiorna mercato", "Refresh market")}</>}
          </Button>
        </div>
      </div>

      <MarketProgress running={running} lang={lang} />

      {/* Config prompt: encourage adding the Booking URL for a homogeneous comparison */}
      {!loading && !data?.has_booking_url && (
        <div data-testid="market-config-prompt" className="mb-5 flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 text-amber-800 px-4 py-3 text-sm dark:border-amber-500/40 dark:bg-amber-500/10 dark:text-amber-300">
          <Info className="w-4 h-4 mt-0.5 shrink-0" />
          <span>{L("Per un confronto accurato, inserisci il link Booking della tua struttura (Modifica struttura) o collega il tuo Channel Manager. Nel frattempo usiamo il tuo prezzo interno come stima provvisoria.",
                   "For an accurate comparison, add your property's Booking link (Edit property) or connect your Channel Manager. Meanwhile we use your internal price as a provisional estimate.")}</span>
        </div>
      )}
      {/* Booking URL is set but the public price hasn't been read yet → prompt to refresh */}
      {!loading && data?.has_booking_url && data?.self_price_provisional && (
        <div data-testid="market-read-prompt" className="mb-5 flex flex-wrap items-center gap-3 rounded-md border border-primary/30 bg-primary/5 text-foreground px-4 py-3 text-sm">
          <Info className="w-4 h-4 shrink-0 text-primary" />
          <span className="flex-1">{L("Link Booking salvato. Clicca “Aggiorna mercato” per leggere il prezzo realmente pubblicato su Booking per queste date. Se la tua struttura risulta al completo per il periodo scelto, il prezzo Booking non sarà disponibile: prova altre date.",
                   "Booking link saved. Click “Refresh market” to read the price actually published on Booking for these dates. If your property is sold out for the selected period, the Booking price won't be available: try other dates.")}</span>
          <Button data-testid="market-read-booking-btn" size="sm" className="h-8" onClick={analyze} disabled={running}>
            {running ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />{L("Lettura...", "Reading...")}</> : <><RefreshCw className="w-4 h-4 mr-2" />{L("Leggi prezzo da Booking", "Read Booking price")}</>}
          </Button>
        </div>
      )}

      {loading ? (
        <p className="text-sm text-muted-foreground py-12 text-center">…</p>
      ) : !data?.has_data ? (
        <div className="text-center py-10" data-testid="market-empty">
          <p className="text-sm text-muted-foreground mb-4">{L("Nessun dato di mercato ancora. Analizza i competitor nella tua zona per ottenere il posizionamento di prezzo.", "No market data yet. Analyze nearby competitors to get your price positioning.")}</p>
          <Button data-testid="market-analyze-empty" onClick={analyze} disabled={running}>
            {running ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />{L("Analisi in corso...", "Analyzing...")}</> : L("Analizza il mercato", "Analyze the market")}
          </Button>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {variant !== "competitors" && (<>
          {/* positioning + range */}
          <div className="lg:col-span-2">
            <div className="flex flex-wrap items-center gap-3 mb-4">
              <span data-testid="market-position" className="text-sm font-bold px-3 py-1.5 rounded-full"
                style={{ backgroundColor: `color-mix(in srgb, ${POS_COLOR[p.position]} 16%, transparent)`, color: POS_COLOR[p.position] }}>
                {p.position_label} · {p.diff_pct >= 0 ? "+" : ""}{p.diff_pct}% {L("vs mediana", "vs median")}
              </span>
              <span className="text-sm text-muted-foreground">{L("La tua tariffa", "Your rate")}: <b className="font-data text-foreground">€{p.my_price}</b></span>
              <span data-testid="market-price-source" className={`text-[11px] px-2 py-0.5 rounded-full ${data.self_price_provisional ? "bg-amber-100 text-amber-800 dark:bg-amber-500/15 dark:text-amber-300" : "bg-emerald-100 text-emerald-800 dark:bg-emerald-500/15 dark:text-emerald-300"}`}>
                {!data.self_price_provisional
                  ? L("prezzo Booking pubblico", "public Booking price")
                  : data.has_booking_url
                    ? L("prezzo Booking non disponibile — usato prezzo interno", "Booking price unavailable — internal used")
                    : L("prezzo interno (provvisorio)", "internal price (provisional)")}
              </span>
              {confMeta && (
                <span data-testid="market-confidence" className="inline-flex items-center gap-1 text-[11px] font-bold px-2 py-0.5 rounded-full"
                  style={{ backgroundColor: `color-mix(in srgb, ${confMeta.color} 16%, transparent)`, color: confMeta.color }}
                  title={conf.note}>
                  <confMeta.icon className="w-3.5 h-3.5" />{L(confMeta.it, confMeta.en)} · {conf.count}
                </span>
              )}
            </div>

            {conf?.level !== "high" && conf?.note && (
              <p className="text-xs text-muted-foreground mb-4 -mt-1">{conf.note}</p>
            )}

            {/* range bar */}
            {(() => {
              const lo = s.min_price, hi = s.max_price, span = Math.max(1, hi - lo);
              const pct = (v) => Math.max(0, Math.min(100, ((v - lo) / span) * 100));
              return (
                <div className="mb-5">
                  <div className="relative h-3 rounded-full bg-secondary mt-8 mb-2">
                    <div className="absolute h-3 rounded-full opacity-40" style={{ left: `${pct(p.recommended_min)}%`, width: `${pct(p.recommended_max) - pct(p.recommended_min)}%`, backgroundColor: "var(--primary)" }} />
                    <div className="absolute -top-6 text-[10px] font-data" style={{ left: `${pct(p.median)}%`, transform: "translateX(-50%)" }}>€{p.median}<div className="w-px h-8 bg-foreground/50 mx-auto" /></div>
                    <div data-testid="market-myprice-marker" className="absolute -bottom-7 text-[10px] font-data font-bold" style={{ left: `${pct(p.my_price)}%`, transform: "translateX(-50%)", color: POS_COLOR[p.position] }}>
                      <div className="w-px h-6 mx-auto" style={{ backgroundColor: POS_COLOR[p.position] }} />€{p.my_price}</div>
                  </div>
                  <div className="flex justify-between text-[11px] text-muted-foreground mt-8"><span>€{s.min_price}</span><span>€{s.max_price}</span></div>
                </div>
              );
            })()}

            <div className="bg-primary/5 border border-primary/20 rounded-lg p-4" data-testid="market-recommended">
              <div className="flex items-center gap-2 text-sm">
                <TrendingUp className="w-4 h-4 text-primary" />
                <span>{L("Range consigliato", "Recommended range")}: <b className="font-data">€{p.recommended_min} – €{p.recommended_max}</b></span>
                <span className="ml-auto text-muted-foreground">{L("target", "target")}: <b className="font-data text-primary">€{p.recommended_price}</b></span>
              </div>
            </div>

            <ul className="mt-4 space-y-2" data-testid="market-reasons">
              {p.reasons.map((r, i) => (
                <li key={i} className="flex items-start gap-2 text-sm text-muted-foreground">
                  <Lightbulb className="w-4 h-4 mt-0.5 text-primary shrink-0" />{r}
                </li>
              ))}
            </ul>
          </div>

          {/* stats */}
          <div className="grid grid-cols-2 gap-3 content-start">
            {[
              { icon: TrendingUp, label: L("Mediana", "Median"), value: `€${s.median_price}` },
              { icon: BarChart3, label: L("Media", "Average"), value: `€${s.avg_price}` },
              { icon: Gauge, label: "Market Pulse", value: s.market_pulse, color: "var(--primary)" },
              { icon: Users, label: L("Simili usati", "Similar used"), value: s.competitor_count },
              { icon: Star, label: L("Rating medio", "Avg rating"), value: s.avg_rating },
              { icon: Award, label: L("Qualità media", "Avg quality"), value: s.avg_quality_score },
            ].map((st, i) => (
              <div key={i} className="bg-secondary/40 border border-border rounded-lg p-3">
                <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-wide text-muted-foreground font-bold mb-1"><st.icon className="w-3.5 h-3.5" />{st.label}</div>
                <div className="text-xl font-data font-bold" style={{ color: st.color }}>{st.value ?? "—"}</div>
              </div>
            ))}
            <p className="col-span-2 text-[11px] text-muted-foreground">{L("Raggio", "Radius")} {s.radius_km} km · {L("mediana/media calcolate solo sui competitor simili selezionati", "median/average computed only on the selected similar competitors")}</p>
          </div>
          </>)}

          {variant !== "positioning" && (<>
          {/* Selected similar competitors */}
          {selected.length > 0 && (
            <div className="lg:col-span-3 mt-2 pt-6 border-t border-border" data-testid="market-similar">
              <div className="flex items-center gap-2 mb-4">
                <Users className="w-4 h-4 text-primary" />
                <h4 className="text-sm font-bold uppercase tracking-wide">{L("Competitor selezionati", "Selected competitors")}</h4>
                <span className="text-xs text-muted-foreground">({selected.length}/5)</span>
                <span className="ml-auto text-[11px] text-muted-foreground">{L("entrano nel calcolo del prezzo", "drive the recommended price")}</span>
              </div>
              {(() => {
                const gp = selected.map((c) => c.price_per_night).filter((v) => v != null);
                const np = selected.map((c) => c.net_price).filter((v) => v != null);
                const avg = (a) => (a.length ? Math.round(a.reduce((x, y) => x + y, 0) / a.length) : null);
                const cg = avg(gp), cn = avg(np);
                const s = data?.self || {};
                return (
                  <div className="grid grid-cols-2 gap-3 mb-3" data-testid="gross-net-compare">
                    <div className="rounded-lg border border-border bg-secondary/30 p-3">
                      <div className="text-[10px] uppercase font-bold text-muted-foreground mb-1">{L("Confronto LORDO · Booking pubblico", "GROSS · public Booking")}</div>
                      <div className="flex justify-between text-sm"><span className="text-muted-foreground">{L("La tua struttura", "You")}</span><span className="font-data font-bold">{s.gross ? `€${s.gross}` : "—"}</span></div>
                      <div className="flex justify-between text-sm"><span className="text-muted-foreground">{L("Media competitor", "Competitor avg")}</span><span className="font-data">{cg ? `€${cg}` : "—"}</span></div>
                    </div>
                    <div className="rounded-lg border border-primary/30 bg-primary/5 p-3">
                      <div className="text-[10px] uppercase font-bold text-primary mb-1">{L("Confronto NETTO · limato da te", "NET · your calibration")}</div>
                      <div className="flex justify-between text-sm"><span className="text-muted-foreground">{L("La tua struttura", "You")}</span><span className="font-data font-bold text-primary">{s.net ? `€${s.net}` : "—"}</span></div>
                      <div className="flex justify-between text-sm"><span className="text-muted-foreground">{L("Media competitor", "Competitor avg")}</span><span className="font-data">{cn ? `€${cn}` : "—"}</span></div>
                    </div>
                  </div>
                );
              })()}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {selected.map((c, i) => (
                  <div key={i} onClick={() => openDetail(c)}
                    data-testid={`similar-competitor-${i}`}
                    title={L("Apri dettaglio competitor", "Open competitor detail")}
                    className="group flex gap-3 bg-secondary/30 hover:bg-secondary/60 border border-border rounded-lg p-3 transition-colors cursor-pointer">
                    <div className="w-16 h-16 rounded-md bg-muted overflow-hidden shrink-0">
                      {c.image
                        ? <img src={c.image} alt={c.name} className="w-full h-full object-cover" loading="lazy" />
                        : <div className="w-full h-full flex items-center justify-center text-muted-foreground"><MapPin className="w-5 h-5" /></div>}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-start justify-between gap-2">
                        <p className="text-sm font-semibold leading-tight truncate group-hover:text-primary transition-colors">{c.name || "—"}</p>
                        <div className="flex items-center gap-1 shrink-0">
                          <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-primary/10 text-primary" data-testid={`similar-score-${i}`}>
                            {c.similarity}% {L("simile", "match")}
                          </span>
                          <button data-testid={`similar-remove-${i}`}
                            title={L("Rimuovi dal calcolo (resta tra gli analizzati)", "Remove from calc (stays in analyzed)")}
                            onClick={(e) => { e.stopPropagation(); toggleExclude(c.competitor_id, true); }}
                            disabled={busyId === c.competitor_id}
                            className="p-1 rounded hover:bg-destructive/10 text-destructive transition-colors disabled:opacity-50">
                            {busyId === c.competitor_id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Ban className="w-3.5 h-3.5" />}
                          </button>
                        </div>
                      </div>
                      <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 mt-1 text-[11px] text-muted-foreground">
                        <span className="font-data font-bold text-foreground">€{c.price_per_night}<span className="font-normal text-muted-foreground"> {L("lordo", "gross")}</span></span>
                        {c.net_price != null && c.net_price !== c.price_per_night && (
                          <span className="font-data font-bold text-primary">€{c.net_price}<span className="font-normal text-muted-foreground"> {L("netto", "net")}</span></span>
                        )}
                        {c.price_diff_pct != null && (
                          <span style={{ color: c.price_diff_pct > 0 ? "var(--demand-high)" : "var(--demand-low)" }}>
                            {c.price_diff_pct > 0 ? "+" : ""}{c.price_diff_pct}%
                          </span>
                        )}
                        {c.distance_km != null && <span className="inline-flex items-center gap-0.5"><MapPin className="w-3 h-3" />{c.distance_km} km</span>}
                        {c.rating != null && <span className="inline-flex items-center gap-0.5"><Star className="w-3 h-3 fill-current" />{c.rating}</span>}
                        {c.stars ? <span>{c.stars}★</span> : null}
                      </div>
                      <div className="flex flex-wrap gap-1 mt-1.5">
                        {c.needs_breakfast_price ? (
                          <span data-testid={`bf-missing-${i}`} className="inline-flex items-center gap-0.5 text-[10px] px-1.5 py-0.5 rounded bg-red-500/15 text-red-600 dark:text-red-400 font-bold">
                            <AlertTriangle className="w-3 h-3" />{c.price_breakfast_included === true ? L("Colazione inclusa: inserisci €", "Breakfast incl.: enter €") : L("Colazione da verificare", "Breakfast to verify")}
                          </span>
                        ) : (c.breakfast_verified && c.breakfast_price != null) ? (
                          <span className="inline-flex items-center gap-0.5 text-[10px] px-1.5 py-0.5 rounded bg-emerald-500/15 text-emerald-600 dark:text-emerald-400 font-bold">
                            <Coffee className="w-3 h-3" />{L("Colazione", "Breakfast")} {c.breakfast_price_estimated ? "~" : ""}€{c.breakfast_price}
                          </span>
                        ) : null}
                        {c.breakfast && c.breakfast_label !== "excluded" && <span className="inline-flex items-center gap-0.5 text-[10px] px-1.5 py-0.5 rounded bg-secondary text-muted-foreground"><Coffee className="w-3 h-3" />{L("Colazione", "Breakfast")}</span>}
                        {c.pool && <span className="inline-flex items-center gap-0.5 text-[10px] px-1.5 py-0.5 rounded bg-secondary text-muted-foreground"><Waves className="w-3 h-3" />Pool</span>}
                        {c.free_cancellation && <span className="text-[10px] px-1.5 py-0.5 rounded bg-secondary text-muted-foreground">{L("Cancellazione gratis", "Free cancel")}</span>}
                        {c.url && (
                          <a href={c.url} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()}
                            data-testid={`similar-booking-link-${i}`}
                            className="inline-flex items-center gap-0.5 text-[10px] px-1.5 py-0.5 rounded text-primary ml-auto hover:underline">
                            <ExternalLink className="w-3 h-3" />Booking
                          </a>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Manage competitors: add manually by Booking URL */}
          <div className="lg:col-span-3 mt-2 pt-6 border-t border-border" data-testid="market-manage">
            <button data-testid="market-manage-toggle" onClick={() => setShowManage((v) => !v)}
              className="w-full flex items-center gap-2 text-left group">
              <ListChecks className="w-4 h-4 text-primary" />
              <h4 className="text-sm font-bold uppercase tracking-wide">{L("Gestisci competitor", "Manage competitors")}</h4>
              <span className="ml-auto inline-flex items-center gap-1 text-xs text-primary group-hover:underline">
                {showManage ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
              </span>
            </button>
            {showManage && (
              <div className="mt-3">
                <p className="text-xs text-muted-foreground mb-2">{L("Aggiungi manualmente un competitor tramite il suo link Booking se il sistema non l'ha individuato.", "Manually add a competitor by its Booking link if the system missed it.")}</p>
                <div className="flex flex-wrap gap-2">
                  <input data-testid="market-add-url" value={addUrl} onChange={(e) => setAddUrl(e.target.value)}
                    placeholder="https://www.booking.com/hotel/..."
                    className="flex-1 min-w-[240px] h-9 rounded-md border border-border bg-background px-3 text-sm" />
                  <Button data-testid="market-add-btn" size="sm" className="h-9" onClick={addByUrl} disabled={adding || !addUrl.trim()}>
                    {adding ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Plus className="w-4 h-4 mr-1" />{L("Aggiungi", "Add")}</>}
                  </Button>
                </div>
              </div>
            )}
          </div>

          {/* Full transparency: all analyzed competitors */}
          {analyzed.length > 0 && (
            <div className="lg:col-span-3 mt-2 pt-6 border-t border-border" data-testid="market-analyzed">
              <button data-testid="market-analyzed-toggle" onClick={() => setShowAnalyzed((v) => !v)}
                className="w-full flex items-center gap-2 text-left group">
                <ListChecks className="w-4 h-4 text-primary" />
                <h4 className="text-sm font-bold uppercase tracking-wide">{L("Competitor analizzati", "Analyzed competitors")}</h4>
                <span className="text-xs text-muted-foreground">({analyzed.length})</span>
                <span className="ml-auto inline-flex items-center gap-1 text-xs text-primary group-hover:underline">
                  {showAnalyzed ? <>{L("Nascondi", "Hide")}<ChevronUp className="w-4 h-4" /></> : <>{L("Mostra tutti", "Show all")}<ChevronDown className="w-4 h-4" /></>}
                </span>
              </button>
              <p className="text-xs text-muted-foreground mt-1.5">{L("Tutte le strutture valutate, con punteggio di similarità e motivo di selezione. Puoi aggiungerle ai selezionati (max 5) o escluderle dal calcolo.", "Every evaluated structure, with similarity score and selection reason. Add them to your selected set (max 5) or exclude from the calc.")} · {L("Selezionati", "Selected")} {selected.length}/5</p>

              {showAnalyzed && (
                <div className="mt-4 overflow-x-auto rounded-lg border border-border">
                  <table className="w-full text-sm" data-testid="analyzed-table">
                    <thead>
                      <tr className="bg-secondary/60 text-[11px] uppercase tracking-wide text-muted-foreground">
                        <th className="text-left font-bold px-3 py-2">{L("Similarità", "Similarity")}</th>
                        <th className="text-left font-bold px-3 py-2">{L("Struttura", "Structure")}</th>
                        <th className="text-right font-bold px-3 py-2">{L("Distanza", "Distance")}</th>
                        <th className="text-right font-bold px-3 py-2">{L("Prezzo", "Price")}</th>
                        <th className="text-right font-bold px-3 py-2">{L("Rating", "Rating")}</th>
                        <th className="text-left font-bold px-3 py-2">{L("Motivo di selezione", "Selection reason")}</th>
                        <th className="px-3 py-2"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {analyzed.map((c, i) => (
                        <tr key={i} data-testid={`analyzed-row-${i}`}
                          className={`border-t border-border ${c.selected ? "" : c.excluded ? "bg-destructive/5" : "bg-muted/30"}`}>
                          <td className="px-3 py-2">
                            <span className="inline-flex items-center gap-1 text-xs font-bold px-2 py-0.5 rounded-full"
                              data-testid={`analyzed-similarity-${i}`}
                              style={{ backgroundColor: c.selected ? "color-mix(in srgb, var(--primary) 14%, transparent)" : "var(--secondary)", color: c.selected ? "var(--primary)" : "var(--muted-foreground)" }}>
                              {c.similarity}%
                            </span>
                          </td>
                          <td className="px-3 py-2">
                            <div className="font-semibold leading-tight max-w-[210px] truncate flex items-center gap-1">
                              {c.name || "—"}
                              {c.selected && <span className="text-[9px] px-1 py-0.5 rounded bg-primary text-primary-foreground uppercase">{L("usato", "used")}</span>}
                              {c.is_manual && <span className="text-[9px] px-1 py-0.5 rounded bg-secondary text-muted-foreground uppercase">{L("manuale", "manual")}</span>}
                            </div>
                            <div className="text-[11px] text-muted-foreground">{c.type || "—"}{c.stars ? ` · ${c.stars}★` : ""}</div>
                          </td>
                          <td className="px-3 py-2 text-right font-data whitespace-nowrap">{c.distance_km != null ? `${c.distance_km} km` : "—"}</td>
                          <td className="px-3 py-2 text-right font-data whitespace-nowrap">
                            {c.price_per_night != null ? (
                              <>€{c.price_per_night}
                                {c.price_diff_pct != null && (
                                  <span className="ml-1 text-[11px]" style={{ color: c.price_diff_pct > 0 ? "var(--demand-high)" : "var(--demand-low)" }}>
                                    {c.price_diff_pct > 0 ? "+" : ""}{c.price_diff_pct}%
                                  </span>
                                )}
                              </>
                            ) : "—"}
                          </td>
                          <td className="px-3 py-2 text-right font-data whitespace-nowrap">
                            {c.rating != null ? <span className="inline-flex items-center gap-0.5"><Star className="w-3 h-3 fill-current text-amber-500" />{c.rating}</span> : "—"}
                            {c.reviews ? <span className="block text-[10px] text-muted-foreground">{c.reviews} {L("recensioni", "reviews")}</span> : null}
                          </td>
                          <td className="px-3 py-2 max-w-[280px]">
                            <span className="text-[12px] text-muted-foreground">{c.included_reason}</span>
                          </td>
                          <td className="px-3 py-2 text-right whitespace-nowrap">
                            <div className="flex items-center justify-end gap-2">
                              {!c.selected && (
                                <button data-testid={`analyzed-add-${i}`} onClick={() => addToMine(c.competitor_id)}
                                  className="inline-flex items-center gap-0.5 text-xs text-emerald-600 hover:underline">
                                  <Plus className="w-3 h-3" />{L("Aggiungi ai selezionati", "Add to selected")}
                                </button>
                              )}
                              <button data-testid={`analyzed-detail-${i}`} onClick={() => openDetail(c)}
                                className="inline-flex items-center gap-0.5 text-xs text-primary hover:underline">
                                <BarChart3 className="w-3 h-3" />{L("Dettaglio", "Detail")}
                              </button>
                              {c.url && (
                                <a href={c.url} target="_blank" rel="noreferrer" data-testid={`analyzed-link-${i}`}
                                  className="inline-flex items-center gap-0.5 text-xs text-muted-foreground hover:underline">
                                  <ExternalLink className="w-3 h-3" />Booking
                                </a>
                              )}
                              <button data-testid={`analyzed-exclude-${i}`}
                                onClick={() => toggleExclude(c.competitor_id, !c.excluded)}
                                disabled={busyId === c.competitor_id}
                                className={`inline-flex items-center gap-0.5 text-xs ${c.excluded ? "text-emerald-600" : "text-destructive"} hover:underline`}>
                                {busyId === c.competitor_id ? <Loader2 className="w-3 h-3 animate-spin" /> : c.excluded ? <><RotateCcw className="w-3 h-3" />{L("Reincludi", "Re-include")}</> : <><Ban className="w-3 h-3" />{L("Escludi", "Exclude")}</>}
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
          </>)}
        </div>
      )}
    </motion.div>
  );
}
